import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Inbox as InboxIcon, SearchOff as SearchOffIcon, Block as BlockIcon, HourglassEmpty as HourglassEmptyIcon } from '@mui/icons-material';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}

export interface EmptyStateProps {
  /** Type of empty state */
  variant?: 'noData' | 'noResults' | 'firstTime' | 'noAccess';
  /** Icon to display */
  icon?: React.ReactNode;
  /** Main title */
  title: string;
  /** Description text */
  description?: string;
  /** Primary action button */
  action?: EmptyStateAction;
  /** Secondary action button */
  secondaryAction?: EmptyStateAction;
  /** Custom sx styles */
  sx?: any;
}

/**
 * EmptyState - A reusable empty state component
 * 
 * Features:
 * - Multiple variants (noData, noResults, firstTime, noAccess)
 * - Optional primary and secondary actions
 * - Customizable icon
 * - Responsive design
 * 
 * @example
 * // No data empty state
 * <EmptyState
 *   variant="noData"
 *   title="ยังไม่มีข้อมูล"
 *   description="เริ่มต้นด้วยการสร้างข้อมูลใหม่"
 *   action={{
 *     label: 'สร้างใหม่',
 *     onClick: handleCreate,
 *     icon: <AddIcon />,
 *   }}
 * />
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'noData',
  icon,
  title,
  description,
  action,
  secondaryAction,
  sx,
}) => {
  // Default icons based on variant
  const defaultIcons = {
    noData: <InboxIcon sx={{ fontSize: 80, opacity: 0.3 }} />,
    noResults: <SearchOffIcon sx={{ fontSize: 80, opacity: 0.3 }} />,
    firstTime: <HourglassEmptyIcon sx={{ fontSize: 80, opacity: 0.3 }} />,
    noAccess: <BlockIcon sx={{ fontSize: 80, opacity: 0.3 }} />,
  };

  const displayIcon = icon || defaultIcons[variant];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
        textAlign: 'center',
        ...sx,
      }}
    >
      {/* Icon */}
      <Box sx={{ mb: 3 }}>{displayIcon}</Box>

      {/* Title */}
      <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 400 }}
        >
          {description}
        </Typography>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {action && (
            <Button
              variant={action.variant || 'contained'}
              color={action.color || 'primary'}
              startIcon={action.icon}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant || 'outlined'}
              color={secondaryAction.color || 'primary'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
